import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getVisitorsList,
    getVisitorSummary,
} from '../../../redux/actions/SocietyPortal/VisitorAction/VisitorAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';

// Query state and data loading for the visitors list, kept out of the screen so
// Visitors.jsx stays composition only.
export const useVisitorList = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const {
        visitorsListData,
        visitorsListLoading,
        visitorsPagination,
        summaryData,
        summaryLoading,
    } = useSelector(state => state.VisitorReducer || {});

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [visitorType, setVisitorType] = useState('');
    const [blockedFilter, setBlockedFilter] = useState('');
    const [page, setPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    // The server runs the search as LIKE %term% across four columns, so a
    // request per keystroke is wasteful — let the typing settle first.
    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(search.trim()); setPage(1); }, 350);
        return () => clearTimeout(t);
    }, [search]);

    const fetchList = useCallback(() => {
        if (!societyId) return;
        dispatch(getVisitorsList({
            // Sent explicitly on purpose: the X-Society-Id header authorises the
            // call but does not scope the rows, so omitting this would return
            // every society's visitors.
            societyId,
            page,
            limit: itemsPerPage,
            search: debouncedSearch || undefined,
            visitorType: visitorType || undefined,
            isBlocked: blockedFilter || undefined,
        }));
    }, [dispatch, societyId, page, itemsPerPage, debouncedSearch, visitorType, blockedFilter]);

    const fetchSummary = useCallback(() => {
        if (societyId) dispatch(getVisitorSummary({ societyId }));
    }, [dispatch, societyId]);

    useEffect(() => { fetchList(); }, [fetchList]);
    useEffect(() => { fetchSummary(); }, [fetchSummary]);

    // Every mutation moves both the current page of rows and the society-wide
    // counters, so the two always refresh together.
    const refresh = useCallback(() => { fetchList(); fetchSummary(); }, [fetchList, fetchSummary]);

    const rows = useMemo(
        () => (Array.isArray(visitorsListData) ? visitorsListData : []),
        [visitorsListData]
    );

    const hasFilters = !!(debouncedSearch || visitorType || blockedFilter);

    const clearFilters = useCallback(() => {
        setSearch('');
        setVisitorType('');
        setBlockedFilter('');
        setPage(1);
    }, []);

    // Page resets live with the setters so no caller can change a filter and
    // leave the user stranded on a page that no longer exists.
    const changeVisitorType = useCallback((v) => { setVisitorType(v); setPage(1); }, []);
    const changeBlockedFilter = useCallback((v) => { setBlockedFilter(v); setPage(1); }, []);
    const changeItemsPerPage = useCallback((n) => { setItemsPerPage(n); setPage(1); }, []);

    return {
        societyId,
        rows,
        loading: !!visitorsListLoading,
        totalPages: visitorsPagination?.totalPages || 1,
        // Read from meta.pagination, never from rows.length — that is only the
        // current page.
        totalItems: visitorsPagination?.total ?? rows.length,

        summary: summaryData,
        summaryLoading: !!summaryLoading,

        search,
        setSearch,
        visitorType,
        setVisitorType: changeVisitorType,
        blockedFilter,
        setBlockedFilter: changeBlockedFilter,
        page,
        setPage,
        itemsPerPage,
        setItemsPerPage: changeItemsPerPage,

        hasFilters,
        clearFilters,
        refresh,
    };
};

export default useVisitorList;
